import { CREDENTIALS_SEARCH_TAB, PATHS } from '~/common/constants'
import {
	GLOBAL_SEARCH_V2_CANDIDATES_SCOPES,
	GLOBAL_SEARCH_V2_JOBS_SCOPES,
	GLOBAL_SEARCH_V2_JOBS_VIEW_SCOPE,
	GLOBAL_SEARCH_V2_TODAY_SCOPES,
	promoteScope,
} from '~/common/constants/global-search.constant'
import { CANDIDATE_TABS, JOB_TABS } from '~/common/constants/tab.constant'
import { classicHostedPath, v2HostedPath } from '~/common/constants/v2-hosted-path'
import { V2_PATHS, V2_BASE } from '../recruiter-v2.constants'

const segments = (pathname = '') => pathname.split('?')[0].split('/').filter(Boolean)

export const isV2Path = (pathname = '') => pathname === V2_BASE || pathname.startsWith(`${V2_BASE}/`)

/**
 * The V1 screen that shows the same thing as a V2 one, so switching version
 * keeps the recruiter where they were instead of dropping them on a home page.
 * A V2 screen with no V1 twin falls back to the V1 dashboard.
 */
const queryOf = (pathname = '') => (pathname.includes('?') ? `?${pathname.split('?')[1]}` : '')

export const classicEquivalent = (pathname = '') => {
	const hosted = classicHostedPath(pathname)
	if (hosted) return hosted
	const [, screen, id] = segments(pathname)

	switch (screen) {
		// The results page keeps its query: switching must not lose the search.
		case 'search':
			return `${PATHS.recruiter.search}${queryOf(pathname)}`
		case 'jobs':
			return id ? `${PATHS.recruiter.job_info}/${id}` : PATHS.recruiter.recruiter_jobs
		case 'candidates': {
			if (!id) return PATHS.recruiter.candidates
			// Keep the tab across the switch: V2's `?tab=` is Classic's path segment.
			// V2 spells two ids with a hyphen (`app-access`) where Classic uses `app_access`.
			const tab = (new URLSearchParams(queryOf(pathname)).get('tab') || '').replace(/-/g, '_')
			return tab ? `${PATHS.recruiter.candidate_info}/${id}/${tab}` : `${PATHS.recruiter.candidate_info}/${id}`
		}
		case 'messages':
			return id ? `${PATHS.recruiter.messages}/${id}` : PATHS.recruiter.messages
		case 'notifications':
			return PATHS.recruiter.notification
		case 'profile':
			return PATHS.recruiter.user_profile
		case 'users':
			return id ? `${PATHS.recruiter.user_info}/${id}` : PATHS.recruiter.role
		default:
			return PATHS.recruiter.home
	}
}

/*
 * Classic names a candidate's tab in the path (`/candidate_info/:id/activities`), V2
 * in the query (`?tab=activities`). Without this the tab was dropped on the way to
 * V2, and every "go to Activities" link opened Profile for a recruiter on V2. V2's
 * tab resolver already reads Classic's tab names, so the name passes through as is.
 */
const withTab = (query, tab) => {
	if (!tab || /[?&]tab=/.test(query)) return query
	return query ? `${query}&tab=${encodeURIComponent(tab)}` : `?tab=${encodeURIComponent(tab)}`
}

/**
 * The V2 screen that shows the same thing as a Classic one, or null when V2 has
 * none. Used to send a recruiter on V2 from a Classic URL (a bookmark, an e-mail
 * link, an old tab) to the same place in V2 — and to leave Classic-only screens
 * (timecards, pay matrix, …) alone instead of dropping them on Today.
 * Detail pages keep their query (`?tab=`), as `jobDetailPath` does on both sides.
 */
export const v2Twin = (pathname = '') => {
	const hosted = v2HostedPath(pathname)
	if (hosted) return hosted
	const [screen, id, tab] = segments(pathname)
	const query = queryOf(pathname)

	switch (`/${screen || ''}`) {
		case '/':
		case PATHS.recruiter.home:
			return V2_PATHS.today
		case PATHS.recruiter.search:
			return `${V2_PATHS.search}${query}`
		case PATHS.recruiter.recruiter_jobs:
			return V2_PATHS.jobs
		case PATHS.recruiter.job_info:
			return id ? `${V2_PATHS.jobDetail(id)}${query}` : V2_PATHS.jobs
		case PATHS.recruiter.candidates:
			return V2_PATHS.candidates
		case PATHS.recruiter.candidate_info:
			return id ? `${V2_PATHS.candidateDetail(id)}${withTab(query, tab)}` : V2_PATHS.candidates
		case PATHS.recruiter.messages:
			return id ? `${V2_PATHS.messages}/${id}` : V2_PATHS.messages
		case PATHS.recruiter.notification:
			return V2_PATHS.notifications
		case PATHS.recruiter.user_profile:
			return V2_PATHS.profile
		case PATHS.recruiter.user_info:
			return id ? V2_PATHS.userDetail(id) : V2_PATHS.today
		default:
			return null
	}
}

/**
 * Where to send a Classic URL for a recruiter on V2, or null to stay. Bookmarks,
 * e-mail links and old tabs still point at Classic screens. `usesV2` = V2 is
 * released for the tenant and the user has not chosen Classic; the ATS-embedded
 * portal is never moved.
 */
export const v2RedirectTarget = (pathname = '', search = '', { usesV2 = false, embedded = false } = {}) => {
	if (!usesV2 || embedded || isV2Path(pathname)) return null
	return v2Twin(`${pathname}${search || ''}`)
}

/**
 * The V2 screen for a V1 one, for the version switch. Where V2 has no screen yet
 * the recruiter lands on Today rather than a dead route.
 */
export const v2Equivalent = (pathname = '') => v2Twin(pathname) ?? V2_PATHS.today

/**
 * The full notification list for the version the recruiter is in. The bell is
 * shared with Classic, so "View all" on a V2 screen must stay in the V2 shell.
 */
export const notificationListPath = (pathname = typeof window !== 'undefined' ? window.location.pathname : '') =>
	isV2Path(pathname) ? V2_PATHS.notifications : PATHS.recruiter.notification

/**
 * Job alerts (SPT-2331) for the version the recruiter is in: the list, New, and one alert's Edit. The same pages
 * render in both shells, so every link on them goes through these.
 */
const currentPathname = () => (typeof window !== 'undefined' ? window.location.pathname : '')
export const jobAlertsPath = (pathname = currentPathname()) => (isV2Path(pathname) ? V2_PATHS.jobAlerts : PATHS.recruiter.job_alerts)
export const jobAlertNewPath = (pathname = currentPathname()) => `${jobAlertsPath(pathname)}/new`
export const jobAlertEditPath = (id, pathname = currentPathname()) => `${jobAlertsPath(pathname)}/${encodeURIComponent(id)}/edit`

/**
 * The internal-user page (recruiter, manager, account manager) for the version
 * the recruiter is in. Opened from a V2 screen it must stay in the V2 shell.
 */
export const userInfoPath = (userId, pathname = typeof window !== 'undefined' ? window.location.pathname : '') =>
	isV2Path(pathname) ? V2_PATHS.userDetail(userId) : `${PATHS.recruiter.user_info}/${encodeURIComponent(userId)}`

/**
 * Where a job's detail lives for the version the recruiter is in. Shared table
 * cells call this at render time, so a job opened from a V2 list stays in V2.
 * `query` is an already-encoded query string ("tab=matches") kept on either side.
 */
export const jobDetailPath = (jobId, { query, pathname = typeof window !== 'undefined' ? window.location.pathname : '' } = {}) => {
	const base = isV2Path(pathname) ? V2_PATHS.jobDetail(jobId) : `${PATHS.recruiter.job_info}/${jobId}`
	return query ? `${base}?${query}` : base
}

/**
 * Where a candidate's detail lives for the version the recruiter is in — the same
 * rule as `jobDetailPath`, so a candidate opened from a V2 list stays in V2.
 */
/**
 * The in-app thread for the version the recruiter is in. The candidate header
 * is shared with Classic, so a Message click on a V2 profile must stay in V2.
 */
export const messageThreadPath = (conversationId, pathname = typeof window !== 'undefined' ? window.location.pathname : '') => {
	const base = isV2Path(pathname) ? V2_PATHS.messages : PATHS.recruiter.messages
	return conversationId ? `${base}/${conversationId}` : base
}

export const candidateDetailPath = (workerId, { query, pathname = typeof window !== 'undefined' ? window.location.pathname : '' } = {}) => {
	const base = isV2Path(pathname) ? V2_PATHS.candidateDetail(workerId) : `${PATHS.recruiter.candidate_info}/${workerId}`
	return query ? `${base}?${query}` : base
}

/**
 * A candidate's Activities tab for the version the recruiter is in. The two
 * versions address the tab differently: a path segment in Classic, `?tab=` in V2.
 */
export const candidateActivitiesPath = (workerId, pathname = typeof window !== 'undefined' ? window.location.pathname : '') =>
	isV2Path(pathname) ? `${V2_PATHS.candidateDetail(workerId)}?tab=activities` : `${PATHS.recruiter.candidate_info}/${workerId}/activities`

/*
 * V2 keeps the board in `?view=` with its own ids (jobs / submissions /
 * assignments). Global search scopes are still keyed on Classic tab paths
 * (`/recruiter_jobs/submission`). Mapping must land on a Classic tab that
 * actually has a scope — otherwise the type-ahead fans out over the page but
 * never promotes the list the recruiter is looking at, and Enter feels random.
 */
const V2_JOBS_VIEW_TO_CLASSIC_TAB = {
	jobs: JOB_TABS.available_jobs,
	submissions: JOB_TABS.submission,
	assignments: JOB_TABS.active_assignments,
	// Legacy / deep-link views that already use Classic tab ids.
	[JOB_TABS.available_jobs]: JOB_TABS.available_jobs,
	[JOB_TABS.shift_jobs]: JOB_TABS.shift_jobs,
	[JOB_TABS.submission]: JOB_TABS.submission,
	[JOB_TABS.matches]: JOB_TABS.matches,
	[JOB_TABS.active_assignments]: JOB_TABS.active_assignments,
	[JOB_TABS.upcoming_assignments]: JOB_TABS.upcoming_assignments,
	[JOB_TABS.closed_jobs]: JOB_TABS.closed_jobs,
	// Older V2 / digest links that used the chip as the view.
	toas: JOB_TABS.active_assignments,
	open: JOB_TABS.available_jobs,
	new: JOB_TABS.available_jobs,
	closed: JOB_TABS.closed_jobs,
	current: JOB_TABS.active_assignments,
	ending: JOB_TABS.active_assignments,
	starting: JOB_TABS.upcoming_assignments,
	past: JOB_TABS.active_assignments,
	all: JOB_TABS.active_assignments,
	internal_submissions: JOB_TABS.submission,
	external_submissions: JOB_TABS.submission,
	active_applications: JOB_TABS.submission,
	awaiting_client: JOB_TABS.submission,
	interview_requested: JOB_TABS.submission,
	offer_pending: JOB_TABS.submission,
	ready_to_send: JOB_TABS.submission,
	with_manager: JOB_TABS.submission,
	sent_back: JOB_TABS.submission,
}

const V2_CANDIDATES_VIEW_TO_CLASSIC_TAB = {
	all: null,
	active: CANDIDATE_TABS.submission_ready,
	new: CANDIDATE_TABS.pre_submission,
	new_assigned: CANDIDATE_TABS.pre_submission,
	profile_incomplete: CANDIDATE_TABS.pre_submission,
	assignments: CANDIDATE_TABS.on_assignment,
	// Classic passthrough.
	[CANDIDATE_TABS.on_assignment]: CANDIDATE_TABS.on_assignment,
	[CANDIDATE_TABS.per_diem_eligible]: CANDIDATE_TABS.per_diem_eligible,
	[CANDIDATE_TABS.submission]: CANDIDATE_TABS.submission,
	[CANDIDATE_TABS.matches]: CANDIDATE_TABS.matches,
	[CANDIDATE_TABS.submission_ready]: CANDIDATE_TABS.submission_ready,
	[CANDIDATE_TABS.pre_submission]: CANDIDATE_TABS.pre_submission,
	[CANDIDATE_TABS.shortlist]: CANDIDATE_TABS.shortlist,
	[CANDIDATE_TABS.dismissed]: CANDIDATE_TABS.dismissed,
}

const withClassicTab = (base, tab) => (tab ? `${base}/${tab}` : base)

/**
 * The Classic path global search should read its context from. Search scopes
 * are keyed on V1 list URLs (`/recruiter_jobs/<tab>`); V2 keeps the tab in
 * `?view=`. A V2 list screen answers with its Classic twin and the Classic tab
 * that owns the same records, so the header search appears on the same screens
 * as in Classic and ranks the list on screen first.
 *
 * Credentials has no Classic twin; its rows are candidates, so it answers with
 * the Candidates page under a tab that has no scope of its own
 * (`CREDENTIALS_SEARCH_TAB`): the candidate sections fan out, the page-wide
 * Candidates section first, and none of them is badged "Current tab". Enter
 * still narrows the credential table via `searchWorkerCredentials.keyword`.
 * Its `?view=` is a credential scope, not a Candidates tab, so it is not carried.
 */
export const searchContextPath = (pathname = '', search = '') => {
	if (!isV2Path(pathname)) return pathname
	const view = new URLSearchParams(search).get('view') || ''
	const path = pathname.replace(/\/+$/, '')

	switch (path) {
		case V2_PATHS.today:
			return PATHS.recruiter.home
		case V2_PATHS.jobs: {
			const tab = V2_JOBS_VIEW_TO_CLASSIC_TAB[view]
			return withClassicTab(PATHS.recruiter.recruiter_jobs, tab === undefined ? view || null : tab)
		}
		case V2_PATHS.candidates: {
			// Submissions / Assignments on Candidates are the Jobs board lists —
			// fan out over those scopes, not the candidate pool tabs.
			if (view === 'submissions') return withClassicTab(PATHS.recruiter.recruiter_jobs, JOB_TABS.submission)
			if (view === 'assignments') return withClassicTab(PATHS.recruiter.recruiter_jobs, JOB_TABS.active_assignments)
			if (V2_JOBS_VIEW_TO_CLASSIC_TAB[view] === JOB_TABS.submission) {
				return withClassicTab(PATHS.recruiter.recruiter_jobs, JOB_TABS.submission)
			}
			const tab = V2_CANDIDATES_VIEW_TO_CLASSIC_TAB[view]
			if (tab === undefined) return withClassicTab(PATHS.recruiter.candidates, view || null)
			return withClassicTab(PATHS.recruiter.candidates, tab)
		}
		case V2_PATHS.credentials:
			return `${PATHS.recruiter.candidates}/${CREDENTIALS_SEARCH_TAB}`
		default:
			return pathname
	}
}

/**
 * V2 type-ahead fan-out — Jobs | Submissions | Assignments | Candidates.
 *
 * Classic's `resolveContextScopes(searchContextPath(...))` would inherit the
 * home Pipeline tab and the Classic Candidates strip (Per Diem, On Assignment,
 * Pipeline). V2 has none of those as top-level boards, so the dropdown must
 * fan out over the V2 tabs instead. Returns null off a list page (results /
 * help / detail) so the tenant default still applies.
 */
export const resolveV2ContextScopes = (pathname = '', search = '') => {
	if (!isV2Path(pathname)) return null

	const view = new URLSearchParams(search).get('view') || ''
	const path = pathname.replace(/\/+$/, '')

	if (path === V2_PATHS.today) return [...GLOBAL_SEARCH_V2_TODAY_SCOPES]

	if (path === V2_PATHS.jobs) {
		return promoteScope([...GLOBAL_SEARCH_V2_JOBS_SCOPES], GLOBAL_SEARCH_V2_JOBS_VIEW_SCOPE[view] || 'jobs', (scope) => scope)
	}

	if (path === V2_PATHS.candidates) {
		// Submissions / Assignments are the same boards as on Jobs — promote that
		// tab, keep the page's three tabs (not Classic candidate pool scopes).
		if (view === 'submissions' || V2_JOBS_VIEW_TO_CLASSIC_TAB[view] === JOB_TABS.submission) {
			return promoteScope([...GLOBAL_SEARCH_V2_CANDIDATES_SCOPES], 'job_submissions', (scope) => scope)
		}
		if (view === 'assignments') {
			return promoteScope([...GLOBAL_SEARCH_V2_CANDIDATES_SCOPES], 'assignments', (scope) => scope)
		}
		return promoteScope([...GLOBAL_SEARCH_V2_CANDIDATES_SCOPES], 'candidates', (scope) => scope)
	}

	if (path === V2_PATHS.credentials) return ['candidates']

	return null
}

/** Scope badged "Current tab" on V2 list screens; null on Today / Credentials. */
export const resolveV2ContextScope = (pathname = '', search = '') => {
	if (!isV2Path(pathname)) return null

	const view = new URLSearchParams(search).get('view') || ''
	const path = pathname.replace(/\/+$/, '')

	if (path === V2_PATHS.jobs) return GLOBAL_SEARCH_V2_JOBS_VIEW_SCOPE[view] || 'jobs'

	if (path === V2_PATHS.candidates) {
		if (view === 'submissions' || V2_JOBS_VIEW_TO_CLASSIC_TAB[view] === JOB_TABS.submission) return 'job_submissions'
		if (view === 'assignments') return 'assignments'
		return 'candidates'
	}

	return null
}

/**
 * Placeholder copy for the header field, keyed by the screen the recruiter is
 * on. A single "Search jobs, candidates, submissions…" on every page reads as
 * decoration; a recruiter on Credentials needs to know Enter will find a person
 * or a licence in the list below.
 */
export const searchPlaceholderKey = (pathname = '') => {
	const path = String(pathname || '').replace(/\/+$/, '')
	if (
		path === V2_PATHS.jobs ||
		path.startsWith(`${V2_PATHS.jobs}/`) ||
		path === PATHS.recruiter.recruiter_jobs ||
		path.startsWith(`${PATHS.recruiter.recruiter_jobs}/`)
	) {
		return 'GLOBAL_SEARCH_PLACEHOLDER_JOBS'
	}
	if (path === V2_PATHS.credentials) return 'GLOBAL_SEARCH_PLACEHOLDER_CREDENTIALS'
	if (
		path === V2_PATHS.candidates ||
		path.startsWith(`${V2_PATHS.candidates}/`) ||
		path === PATHS.recruiter.candidates ||
		path.startsWith(`${PATHS.recruiter.candidates}/`)
	) {
		return 'GLOBAL_SEARCH_PLACEHOLDER_CANDIDATES'
	}
	if (path === V2_PATHS.today || path === PATHS.recruiter.home) return 'GLOBAL_SEARCH_PLACEHOLDER_TODAY'
	return 'GLOBAL_SEARCH_PLACEHOLDER'
}

/**
 * A search suggestion's link, in the version the recruiter is in. The server
 * builds Classic URLs; opened from V2, a job's or a candidate's detail opens in
 * V2 instead of dropping the recruiter out of it. Anything V2 has no screen for
 * is left as is.
 */
export const suggestionPath = (url = '', { pathname = typeof window !== 'undefined' ? window.location.pathname : '' } = {}) => {
	if (!isV2Path(pathname)) return url
	const [path, query] = url.split('?')
	const [screen, id] = segments(path)
	if (`/${screen}` === PATHS.recruiter.job_info && id) return query ? `${V2_PATHS.jobDetail(id)}?${query}` : V2_PATHS.jobDetail(id)
	if (`/${screen}` === PATHS.recruiter.candidate_info && id) return candidateDetailPath(id, { query, pathname })
	return url
}
